import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Book, ArrowLeft, Loader, AlertCircle, Save, User, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Course Section Edit Page
 * Allows admins to edit course section details
 */
const CourseSectionEditPage = () => {
  const { courseId, sectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [section, setSection] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [professorId, setProfessorId] = useState('');
  const [semester, setSemester] = useState('');
  const [year, setYear] = useState('');
  
  // Available semesters
  const semesterOptions = ['Fall', 'Spring', 'Summer', 'Winter'];
  
  // Generate year options (current year +/- 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  
  useEffect(() => {
    // Redirect if user is not admin or moderator
    if (user && user.role !== 'admin' && user.role !== 'moderator') {
      toast.error('You do not have permission to access this page');
      navigate('/courses');
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch course data
        const courseRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/courses/${courseId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!courseRes.ok) {
          throw new Error('Failed to fetch course details');
        }
        
        const courseData = await courseRes.json();
        setCourse(courseData);
        
        // Find the correct section
        const sectionData = courseData.sections?.find(s => s.id === sectionId);
        
        if (!sectionData) {
          throw new Error('Section not found');
        }
        
        setSection(sectionData);
        
        // Set initial form values
        setProfessorId(sectionData.professorId || '');
        setSemester(sectionData.semester || '');
        setYear(sectionData.year?.toString() || '');
        
        // Fetch professors for this school
        const professorsRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools/${courseData.schoolId}/professors`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!professorsRes.ok) {
          throw new Error('Failed to fetch professors');
        }
        
        const professorsData = await professorsRes.json();
        setProfessors(professorsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        toast.error('Failed to load section data');
      } finally {
        setLoading(false);
      }
    };
    
    if (courseId && sectionId && user) {
      fetchData();
    }
  }, [courseId, sectionId, user, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!professorId || !semester || !year) {
      toast.error('All fields are required');
      return;
    }
    
    try {
      setSaving(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/course-sections/${sectionId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            professorId,
            semester,
            year: parseInt(year, 10)
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update course section');
      }
      
      toast.success('Course section updated successfully');
      
      // Navigate back to the course details page
      navigate(`/courses/${courseId}`);
    } catch (err) {
      console.error('Error updating course section:', err);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading course section data...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Course Section
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Go Back
        </button>
      </div>
    );
  }
  
  if (!course || !section) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Course Section Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The course section you're trying to edit couldn't be found.
        </p>
        <Link
          to="/admin/courses"
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Courses
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/admin" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Dashboard
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to="/admin/courses" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Courses
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to={`/courses/${courseId}`} className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          {course.code}
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          Edit Section
        </span>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mr-4">
            <Book size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Edit Course Section
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {course.code}: {course.name} - {section.semester} {section.year}
            </p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <label htmlFor="professor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Professor *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="professor"
                  value={professorId}
                  onChange={(e) => setProfessorId(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Professor</option>
                  {professors.map((professor) => (
                    <option key={professor.id} value={professor.id}>
                      {professor.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="semester" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Semester *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="semester"
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Semester</option>
                  {semesterOptions.map((sem) => (
                    <option key={sem} value={sem}>
                      {sem}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Year *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((yearOption) => (
                    <option key={yearOption} value={yearOption}>
                      {yearOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                saving ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <Save className="mr-2 -ml-1 h-5 w-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseSectionEditPage;